<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import type { NextStep } from '../constants/investFlow'

// "WHAT HAPPENS NEXT" timeline on Order Confirmed (Figma 32590:69565):
// done steps tick green, the current one is a violet ring, the rest numbered.

interface Props {
  steps: NextStep[]
}

defineProps<Props>()

const LABEL_COLOR: Record<NextStep['state'], string> = {
  completed: 'default',
  active: 'primary',
  upcoming: 'muted',
}
</script>

<template>
  <Box direction="col" gap="6" class="next-steps">
    <Text type="caption" weight="bold" color="var(--p-surface-400)" is="h4">WHAT HAPPENS NEXT</Text>
    <Box is="ol" direction="col" class="next-steps__list">
      <Box
        v-for="(step, index) in steps"
        :key="step.label"
        is="li"
        gap="10px"
        class="next-steps__step"
        :class="{ 'next-steps__step--last': index === steps.length - 1 }"
      >
        <Box direction="col" align="center" class="next-steps__rail">
          <Box
            v-if="step.state === 'completed'"
            align="center"
            justify="center"
            class="next-steps__done"
            ><PiIcon name="check" class="next-steps__done-icon"
          /></Box>
          <Box
            v-else-if="step.state === 'active'"
            align="center"
            justify="center"
            class="next-steps__active"
          >
            <Box class="next-steps__active-ring" />
          </Box>
          <Box v-else align="center" justify="center" class="next-steps__upcoming">
            <Text type="micro" color="muted" is="span" style="font-size: 8px">{{ index + 1 }}</Text>
          </Box>
          <Box v-if="index < steps.length - 1" class="next-steps__connector" />
        </Box>
        <Box direction="col" gap="6px" class="next-steps__text">
          <Text type="caption" weight="bold" :color="LABEL_COLOR[step.state]" is="span">
            {{ step.label }}
          </Text>
          <Text
            type="body"
            weight="medium"
            :color="step.state === 'upcoming' ? 'var(--p-surface-400)' : 'muted'"
            is="span"
          >
            {{ step.description }}
          </Text>
        </Box>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.next-steps {
  width: 100%;
  padding: 20px;
  border: 1px solid var(--p-surface-100);
  border-radius: 20px;
  background: var(--p-surface-0);
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.03));
}

.next-steps__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.next-steps__step {
  min-height: 80px;
}

.next-steps__step--last {
  min-height: 0;
}

.next-steps__rail {
  flex-shrink: 0;
  width: 34px;
}

.next-steps__connector {
  flex: 1;
  width: 1px;
  background: var(--p-surface-200);
}

.next-steps__done {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--p-success-500);
}

.next-steps__done-icon {
  font-size: 9px;
  color: var(--p-surface-0);
}

.next-steps__active {
  width: 18px;
  height: 18px;
  border-radius: 100px;
  background: var(--p-primary-600);
}

.next-steps__active-ring {
  width: 10px;
  height: 10px;
  border: 2px solid var(--p-primary-200);
  border-radius: 100px;
}

.next-steps__upcoming {
  width: 18px;
  height: 18px;
  border-radius: 100px;
  background: var(--p-surface-200);
}

.next-steps__text {
  flex: 1;
  min-width: 0;
  padding-bottom: 16px;
}
</style>
